/**
 * Adobe Analytics / Omniture SiteCatalyst integration
 * @module
 */

var _ = require('optimizely-client-fn');

var JSONEnv = require('core/lib/json');
var Logger = require('core/utils/logger');
var Poll = require('core/utils/poll');
var RedirectUtil = require('core/utils/redirect');
var trackingPolicies = require('core/constants/tracking_policies');
var WindowEnv = require('core/env/window');

var DEFAULT_SVARIABLE = 's';
var EVAR_LIMIT = 255;
var OTHER_LINK_TYPE_CONSTANT = 'o';
var VENDOR = 'adobe_analytics';
var OPTIMIZELY_LAYER_EVENT_CONSTANT = 'optimizelyLayerEvent';
var OPTIMIZELY_LAYER_DECISION_CONSTANT = 'OptimizelyLayerDecision';
var di = require('core/di');
var generateAnalyticsValueString = require('core/plugins/helpers/generate_analytics_value');
var IntegrationSettingsStore = di.get('stores/integration_settings');

/**
 * Fetch the Adobe Analytics object
 *
 * Typically, this object is stored on a global named "s", but some AA customers
 * can define a custom one to avoid clashing with other globals or have something
 * slightly more descriptive than "s".
 *
 * Preferentially get from:
 * 1. settings.sVariableReference - a reference to an object, set by the v1.integrationSettings API
 * 2. window[settings.sVariable]
 * 3. window[DEFAULT_SVARIABLE]
 *
 * @param {Object} WindowEnv
 * @param {Object} settings used by the Adobe integration
 * @param {Object} [settings.sVariableReference]
 * @param {string} [settings.sVariable]
 * @returns {Object|null} the Adobe Analytics object, or null, if none is found.
 * @private
 */
function getAdobeAnalyticsObject(window, adobeSettings): any|null {
  if (adobeSettings['sVariableReference']) {
    return adobeSettings['sVariableReference'] || null;
  }
  var sVariableName = !!adobeSettings['sVariable'] ? adobeSettings['sVariable'] : DEFAULT_SVARIABLE;
  return window.getGlobal(sVariableName);
}

/**
 * In order for custom link tracking to work, the following keys
 * must be present on the object. This simply adds them if they
 * do not exist as it is possible that the customer has already added the
 * required parameters to the object, in which case, we do not want to
 * modify them.
 *
 * @param {Object} adobeAnalyticsObject
 * @private
 */
function addRequiredAdobeKeys(adobeAnalyticsObject:any) {
  var requiredKeys = ['events', 'linkTrackEvents', 'linkTrackVars'];
  _.each(requiredKeys, function(key) {
    if (!_.isString(adobeAnalyticsObject[key])) {
      adobeAnalyticsObject[key] = '';
    }
  });
}

/**
 * When a layer decision is made:
 *  1. Determine what Adobe Analytics / SiteCatalyst to use (poll until it becomes available)
 *  2. Generate a string to populate the desired props / eVars with
 *  3. Call tl() like so:
 *
 * ```
 * tl(true, 'o', link_name)
 * ```
 *
 * Where "true" indicates that it is not referring to a href attribute, 'o' refers to it being a "general / other"
 * type of link, and link_name is the name of the link.
 *
 * Unlike the legacy A/B version, which required loading of the optimizely snippet, then s_code, then
 * a manual activation call, then an Adobe specific call, this strives to be as agnostic as possible
 * in terms of execution ordering.
 * This is achieved through polling and through using the tl function, as opposed to the t function.
 * In theory, tl can be called multiple times without any ill effects on Adobe metrics.
 * @param {DecisionData} decisionData
 * @return {Promise} Resolves when tracked with Adobe Analytics API, or error occurs (in which case the error is logged)
 */
var trackLayerDecisionAdobe = function(decisionData:DecisionData): Promise<any> {
  var settings = decisionData.integrationSettings;
  if (!settings || !settings[VENDOR]) {
    return;
  }

  if (!decisionData.experimentId || !decisionData.variationId) {
    return;
  }

  var layer = {
    id: decisionData.layerId,
    name: decisionData.layerName,
    policy: decisionData.layerPolicy,
    integrationStringVersion: decisionData.integrationStringVersion,
  };

  var experiment = {
    id: decisionData.experimentId,
    name: decisionData.experimentName,
  };

  var variation = {
    id: decisionData.variationId,
    name: decisionData.variationName,
  };

  var isLayerHoldback = decisionData.isLayerHoldback;

  var decisionString = generateAnalyticsValueString.generateAnalyticsString(layer, experiment, variation, isLayerHoldback, EVAR_LIMIT, true);

  if (!decisionString) {
    return;
  }

  return trackDecisionWithAdobeAnalytics(decisionString, settings[VENDOR])['catch'](function(e) {
    Logger.warn('Tracker for', VENDOR, 'failed:', e);
  });
};

var trackDecisionWithAdobeAnalytics = function(decisionString, layerVendorIntegrationSettings) {
  var MAX_RETRIES = 50,
      WAIT_TIME = __TEST__ ? 5 : 200;

  var settings;
  return Poll.pollFor(function isReadyToTrack() {
    // Check if page-specific integration settings (most notably, a direct `sVariableReference`) were provided via the JS API on this page load
    var globalSettings = IntegrationSettingsStore.getReference(VENDOR);
    settings = _.extend({}, globalSettings, layerVendorIntegrationSettings);
    return getAdobeAnalyticsObject(WindowEnv, settings);
  }, WAIT_TIME, MAX_RETRIES)
  .then(function track(adobeAnalyticsObject) {
    var eVar;
    var sProp;

    var eVarValue = settings.site_catalyst_eVar || null;
    var sPropValue = settings.site_catalyst_prop || null;

    // prepend eVar and sProp to properly set adobe analytics global object
    if (eVarValue) {
      eVar = 'eVar' + eVarValue;
    }
    if (sPropValue) {
      sProp = 'prop' + sPropValue;
    }

    addRequiredAdobeKeys(adobeAnalyticsObject);
    var tempVars = adobeAnalyticsObject.linkTrackVars.split(',');

    if (eVar) {
      decisionString = decisionString.substring(0, EVAR_LIMIT);
      adobeAnalyticsObject[eVar] = decisionString;
      if (tempVars.indexOf(eVar) === -1) {
        tempVars.push(eVar);
      }
    }

    if (sProp) {
      adobeAnalyticsObject[sProp] = decisionString;
      if (tempVars.indexOf(sProp) === -1) {
        tempVars.push(sProp);
      }
    }

    // If there is effectiveReferrer then user was redirected from another page/experiment.
    // We need to use that page's referrer for correct tracking.
    var effectiveReferrer = RedirectUtil.getReferrer();
    if (effectiveReferrer) {
      adobeAnalyticsObject.referrer = effectiveReferrer;
    }

    if (adobeAnalyticsObject.linkTrackVars.indexOf('events') === -1) {
      tempVars.push('events');
    }

    // Build tracking variable string
    adobeAnalyticsObject.linkTrackVars = _.filter(tempVars).join(',');

    // Add the event to the tracked events
    if (adobeAnalyticsObject.linkTrackEvents.indexOf(OPTIMIZELY_LAYER_EVENT_CONSTANT) === -1) {
      var tempTrackEvents = adobeAnalyticsObject.linkTrackEvents.split(',');
      tempTrackEvents.push(OPTIMIZELY_LAYER_EVENT_CONSTANT);
      adobeAnalyticsObject.linkTrackEvents = _.filter(tempTrackEvents).join(',');
    }

    // Add our new event
    if (adobeAnalyticsObject.events.indexOf(OPTIMIZELY_LAYER_EVENT_CONSTANT) === -1) {
      var tempEvents = adobeAnalyticsObject.events.split(',');
      tempEvents.push(OPTIMIZELY_LAYER_EVENT_CONSTANT);
      adobeAnalyticsObject.events = _.filter(tempEvents).join(',');
    }

    // Make the link tracking call
    adobeAnalyticsObject.tl(
        true, OTHER_LINK_TYPE_CONSTANT, OPTIMIZELY_LAYER_DECISION_CONSTANT);
  });
};


var trackerAdobe = {
  preRedirectPolicy: trackingPolicies.PreRedirectPolicies.PERSIST_BEFORE_REDIRECT,
  postRedirectPolicy: trackingPolicies.PostRedirectPolicies.TRACK_IMMEDIATELY,
  nonRedirectPolicy: trackingPolicies.NonRedirectPolicies.TRACK_IMMEDIATELY,
  trackLayerDecision: trackLayerDecisionAdobe,
  serializeSettings: JSONEnv.stringify,
  deserializeSettings: JSONEnv.parse,
};

module.exports = function(PluginManager) {
  PluginManager.registerAnalyticsTracker('adobe_analytics', trackerAdobe);
};


if (__TEST__) {
  module.exports.plugin = trackerAdobe;
  module.exports.getAdobeAnalyticsObject = getAdobeAnalyticsObject;
  module.exports.addRequiredAdobeKeys = addRequiredAdobeKeys;
}
